import React, { useState } from 'react';
import { View, Text, Pressable, TextInput } from '../lib/react-native-compat';

// Shared toolbar pieces for the procurement tabs (Uncommitted, Dates): the chip
// button, the multi-select dropdown, and the loading-skeleton bars. Both tabs
// draw a single-row filter bar out of these so the two toolbars stay identical.

// Label at the start of a toolbar group ("SCOPE", "WINDOW").
export const barLabel = {
    fontSize: 11, fontWeight: '700', color: '#64748b',
    marginRight: 8, marginBottom: 6,
};

// Thin vertical rule between toolbar groups.
export const Divider = () => (
    <View style={{ width: 1, height: 18, backgroundColor: '#e2e8f0', marginRight: 12, marginLeft: 6, marginBottom: 6 }} />
);

export const Chip = ({ label, active, onPress }) => (
    <Pressable
        onPress={onPress}
        style={{
            paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10,
            borderRadius: 4, borderWidth: 1, marginRight: 6, marginBottom: 6,
            borderColor: active ? '#0f172a' : '#cbd5e1',
            backgroundColor: active ? '#0f172a' : '#fff',
        }}
    >
        <Text style={{ fontSize: 12, color: active ? '#fff' : '#334155' }}>{label}</Text>
    </Pressable>
);

// Small outline button ("Refresh", "Export to Excel").
export const OutlineButton = ({ label, onPress, disabled }) => (
    <Pressable
        onPress={onPress}
        disabled={disabled}
        style={{
            paddingTop: 5, paddingBottom: 5, paddingLeft: 12, paddingRight: 12,
            // Pressable's style converter doesn't add borderStyle the way View
            // does — without it the outline never renders and the button reads
            // as floating text.
            borderRadius: 4, borderWidth: 1, borderStyle: 'solid', borderColor: '#0f172a', marginBottom: 6,
            opacity: disabled ? 0.4 : 1,
        }}
    >
        <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600', whiteSpace: 'nowrap' }}>{label}</Text>
    </Pressable>
);

/**
 * Multi-select dropdown filter. The button shows how many values are picked
 * (or a caller-supplied summary, e.g. the selected team's name); the panel
 * lists every option, each with its row count when one is provided. Lists
 * longer than 10 get a type-to-filter search box pinned above the scrolling
 * options. A fixed full-screen layer behind the panel catches the click that
 * closes it.
 */
// `onToggleExclude` opts a dropdown into include/exclude mode: the ticked
// values are the ones filtered OUT, which is how you say "everything except
// these one or two codes" without ticking a hundred boxes.
export const FilterDropdown = ({ label, summary, options, selected, onToggle, onClear, renderLabel, emptyText, exclude, onToggleExclude }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const active = selected.length > 0;
    // Long lists get a search box; short ones stay a plain picklist.
    const searchable = options.length > 10;
    const q = query.trim().toLowerCase();
    const shown = searchable && q
        ? options.filter((o) => `${o.value} ${o.desc || ''}`.toLowerCase().includes(q)
            || (renderLabel && String(renderLabel(o)).toLowerCase().includes(q)))
        : options;
    return (
        <View style={{ position: 'relative', marginRight: 6, marginBottom: 6, zIndex: open ? 30 : 1 }}>
            <Pressable
                onPress={() => { setOpen(!open); setQuery(''); }}
                style={{
                    flexDirection: 'row', alignItems: 'center',
                    paddingTop: 4, paddingBottom: 4, paddingLeft: 10, paddingRight: 10,
                    borderRadius: 4, borderWidth: 1,
                    borderColor: active || open ? '#0f172a' : '#cbd5e1',
                    backgroundColor: '#fff',
                }}
            >
                <Text style={{ fontSize: 12, color: '#334155', fontWeight: active ? '600' : '400' }}>
                    {summary || `${label}${active ? ` (${selected.length})` : ''}`} {open ? '▴' : '▾'}
                </Text>
            </Pressable>
            {open ? (
                <>
                    <Pressable
                        onPress={() => { setOpen(false); setQuery(''); }}
                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, cursor: 'default' }}
                    />
                    <View
                        style={{
                            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 20,
                            backgroundColor: '#fff', borderWidth: 1, borderColor: '#cbd5e1', borderRadius: 6,
                            minWidth: 280,
                            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                            paddingTop: 4, paddingBottom: 4,
                        }}
                    >
                        {searchable ? (
                            <View style={{ paddingTop: 4, paddingBottom: 6, paddingLeft: 8, paddingRight: 8, borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                                <TextInput
                                    value={query}
                                    onChangeText={setQuery}
                                    placeholder={`Search ${label ? label.toLowerCase() : 'options'}...`}
                                    autoFocus
                                    style={{
                                        fontSize: 12, borderRadius: 4, borderColor: '#cbd5e1',
                                        paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 8,
                                        width: '100%',
                                    }}
                                />
                            </View>
                        ) : null}
                        {onToggleExclude ? (
                            <View style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, borderBottomWidth: 1, borderColor: '#f1f5f9' }}>
                                {[{ v: false, t: 'Only these' }, { v: true, t: 'All except these' }].map((m) => (
                                    <Pressable
                                        key={m.t}
                                        onPress={() => onToggleExclude(m.v)}
                                        style={{
                                            paddingTop: 3, paddingBottom: 3, paddingLeft: 8, paddingRight: 8, marginRight: 6,
                                            borderRadius: 999, borderWidth: 1,
                                            borderColor: !!exclude === m.v ? '#0f172a' : '#cbd5e1',
                                            backgroundColor: !!exclude === m.v ? '#0f172a' : '#fff',
                                        }}
                                    >
                                        <Text style={{ fontSize: 11, fontWeight: '600', color: !!exclude === m.v ? '#fff' : '#475569' }}>{m.t}</Text>
                                    </Pressable>
                                ))}
                            </View>
                        ) : null}
                        {active ? (
                            <Pressable
                                onPress={onClear}
                                style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12, borderBottomWidth: 1, borderColor: '#f1f5f9' }}
                            >
                                <Text style={{ fontSize: 12, color: '#0f172a', fontWeight: '600' }}>Clear selection</Text>
                            </Pressable>
                        ) : null}
                        {shown.length === 0 ? (
                            <Text style={{ fontSize: 12, color: '#94a3b8', paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12 }}>
                                {options.length === 0 ? (emptyText || 'No options') : 'No matches'}
                            </Text>
                        ) : null}
                        <View style={{ maxHeight: 260, overflowY: 'auto' }}>
                            {shown.map((o) => {
                                const on = selected.includes(o.value);
                                return (
                                    <Pressable
                                        key={o.value}
                                        onPress={() => onToggle(o.value)}
                                        style={{
                                            flexDirection: 'row', alignItems: 'center',
                                            paddingTop: 6, paddingBottom: 6, paddingLeft: 12, paddingRight: 12,
                                            backgroundColor: on ? '#f1f5f9' : 'transparent',
                                        }}
                                    >
                                        <View
                                            style={{
                                                width: 14, height: 14, borderRadius: 3, borderWidth: 1.5, marginRight: 8,
                                                alignItems: 'center', justifyContent: 'center',
                                                borderColor: on ? '#0f172a' : '#475569',
                                                backgroundColor: on ? '#0f172a' : '#fff',
                                            }}
                                        >
                                            {on ? <Text style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</Text> : null}
                                        </View>
                                        <Text style={{ fontSize: 12, color: '#1e293b', flexGrow: 1, whiteSpace: 'nowrap' }}>
                                            {renderLabel ? renderLabel(o) : o.value}
                                        </Text>
                                        {o.count !== undefined ? (
                                            <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 12 }}>{o.count}</Text>
                                        ) : null}
                                    </Pressable>
                                );
                            })}
                        </View>
                    </View>
                </>
            ) : null}
        </View>
    );
};

// A single pulsing placeholder bar (keyframe lives in public/index.html).
export const Skel = ({ width = '60%', style }) => (
    <View
        style={{
            height: 10, borderRadius: 4, backgroundColor: '#e2e8f0', width,
            animation: 'skeletonPulse 1.4s ease-in-out infinite',
            ...style,
        }}
    />
);

/**
 * Placeholder table body: rows of bars laid out with the real column styles,
 * so the table's shape is visible before the first byte of data lands. Bar
 * widths vary deterministically so the block doesn't read as a solid grid.
 */
export const SkeletonRows = ({ cells, rows = 6 }) => (
    <>
        {Array.from({ length: rows }, (_, i) => (
            <View
                key={i}
                style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 9, paddingBottom: 9 }}
            >
                {cells.map((c, j) => (
                    <View key={j} style={{ ...c, paddingLeft: 8, paddingRight: 8 }}>
                        <Skel width={`${50 + ((i * 7 + j * 13) % 40)}%`} />
                    </View>
                ))}
            </View>
        ))}
    </>
);

// Loading placeholder for a headline stat (label + big number).
export const SkeletonStat = () => (
    <View style={{ marginRight: 28 }}>
        <Skel width={70} style={{ height: 8, marginBottom: 8 }} />
        <Skel width={54} style={{ height: 16 }} />
    </View>
);
